import React, { useState, useEffect, useRef } from 'react'
import { View, StyleSheet, Platform } from 'react-native'
import PropTypes from 'prop-types'
import { Calendar as NativeCalendar, CalendarProvider, ExpandableCalendar, WeekCalendar } from 'react-native-calendars'
import moment from 'moment'

import Txt from '../../../../components/Txt'
import { TouchableOpacity } from 'react-native-gesture-handler'
import Identity from '../../../../utils/Identity'
import { Card } from 'react-native-elements'
import Icon from '../../../../components/Icons'
import { useTheme } from '../../../../theming/ThemeProvider'
import { useSelector } from 'react-redux'
import { usePlatforms } from '../../../../hooks/usePlatforms'

export default function Calendar (props) {
  const {
    events,
    weekView,
    onPressView,
    noShadow,
    currentDate,
    onChangeDate
  } = props

  const [calendarMarkers, setCalendarMarkers] = useState({})
  const today = useRef(moment(new Date(), 'YYYY/MM/DD').format('YYYY-MM-DD'))
  const { theme: appTheme } = useSelector(state => state.globalThemeReducer)
  const { theme } = useTheme()
  const styles = useStyles(theme)
  const { isTablet } = usePlatforms()

  useEffect(() => {
    const markers = {}
    events.forEach((day) => {
      const date = moment(day.date, 'YYYY/MM/DD').format('YYYY-MM-DD')
      markers[date] = { marked: true }
    })
    setCalendarMarkers(markers)
  }, [events, theme])

  const onBackToToday = () => onChangeDate(new Date())

  const calendarTheme = {
    textDayFontSize: 12,
    textDayFontWeight: 'normal',
    'stylesheet.day.single': {
      week: {
        maxHeight: 40
      }
    },
    'stylesheet.calendar.header': {
      monthText: {
        ...Identity.Font(isTablet).body2Bold,
        color: theme.BUNKER
      }
    },
    'stylesheet.calendar.main': {
      container: {
        backgroundColor: theme.CARD_BACKGROUND
      },
      monthView: {
        backgroundColor: theme.CARD_BACKGROUND
      },
      week: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        marginTop: 4,
        marginBottom: 4
      }
    },
    'stylesheet.day.basic': {
      text: {
        marginTop: 5,
        ...Identity.Font(isTablet).body2,
        fontSize: 13,
        color: theme.BUNKER
      },
      today: {
        backgroundColor: `${theme.LOCHMARA}33`,
        borderRadius: 50
      },
      selected: {
        backgroundColor: theme.LOCHMARA,
        borderRadius: 50
      },
      todayText: {
        color: theme.BUNKER
      },
      selectedText: {
        color: theme.WHITE_TEXT
      },
      disabledText: {
        color: theme.SECOND_DISABLED_COLOR
      },
      dot: {
        width: 5,
        height: 5,
        borderRadius: 10,
        opacity: 0
      },
      visibleDot: {
        opacity: 1,
        backgroundColor: theme.SANDY_BROWN
      },
      disabledDot: {
        backgroundColor: theme.SANDY_BROWN
      }
    }
  }

  const weekCalendarTheme = {
    ...calendarTheme,
    'stylesheet.calendar.main': {
      ...calendarTheme['stylesheet.calendar.main'],
      week: {
        ...calendarTheme['stylesheet.calendar.main'].week,
        paddingBottom: 10
      }
    },
    'stylesheet.expandable.main': {
      container: {
        backgroundColor: theme.CARD_BACKGROUND
      }
    }
  }

  const MobileCalendar = Platform.OS === 'web' ? WeekCalendar : ExpandableCalendar

  const isCurrentDay = today.current === currentDate

  return (
    <View style={styles.container}>
      <View
        style={[
          { borderRadius: 5 },
          !noShadow && Identity.Shadow(theme).Card
        ]}
      >
        <Card containerStyle={styles.calendarCard}>
          {weekView
            ? (
              <>
                <View style={styles.weekCalendarHeader}>
                  <TouchableOpacity activeOpacity={0.8} onPress={onPressView}>
                    <Txt
                      text='View'
                      identity='body2'
                      style={styles.viewText}
                      color='OUTER_SPACE'
                    />
                  </TouchableOpacity>
                  {Platform.OS === 'web'
                    ? (
                      <Txt
                        text={moment(currentDate, 'YYYY-MM-DD').format('MMMM')}
                        identity='body2Bold'
                        style={styles.viewText}
                        color='OUTER_SPACE'
                      />) : null}
                  {!isCurrentDay ? (
                    <TouchableOpacity activeOpacity={0.8} onPress={onBackToToday}>
                      <Txt
                        text='Back to today'
                        identity='body2'
                        style={styles.viewText}
                        color='LOCHMARA'
                      />

                    </TouchableOpacity>) : <View />}
                </View>
                <CalendarProvider
                  onDateChanged={onChangeDate}
                  date={currentDate}
                  key={appTheme}
                >
                  <MobileCalendar
                    current={currentDate}
                    date={currentDate}
                    hideKnob
                    disablePan
                    disableWeekScroll
                    theme={weekCalendarTheme}
                    allowShadow={false}
                    markedDates={calendarMarkers}
                    headerStyle={{
                      backgroundColor: theme.CARD_BACKGROUND
                    }}
                    renderArrow={(direction) => (
                      <Icon
                        name={direction === 'right' ? 'AngleRight' : 'AngleLeft'}
                        width={15}
                        height={15}
                        fill={theme.BUNKER}
                      />
                    )}
                  />
                </CalendarProvider>
              </>)
            : (
              <>
                {/* <TouchableOpacity activeOpacity={0.8} onPress={xonBackToToday} disabled={isCurrentDay}>
                  <Txt
                    text='Back to today'
                    identity='body2'
                    style={styles.backToToday}
                    color={isCurrentDay ? 'ATHENS_GRAY' : 'LOCHMARA'}
                  />
                </TouchableOpacity> */}
                <NativeCalendar
                  key={appTheme}
                  date={currentDate}
                  theme={calendarTheme}
                  markedDates={{
                    ...calendarMarkers,
                    [currentDate]: {
                      ...calendarMarkers[currentDate],
                      selected: true
                    }
                  }}
                  onDayPress={onChangeDate}
                  current={currentDate}
                  renderArrow={(direction) => (
                    <Icon
                      name={direction === 'right' ? 'AngleRight' : 'AngleLeft'}
                      width={15}
                      height={15}
                      fill={theme.BUNKER}
                    />
                  )}
                />
              </>)}
        </Card>
      </View>
    </View>
  )
}

const useStyles = (theme) => StyleSheet.create({
  container: {
    paddingVertical: 10
  },
  calendarCard: {
    margin: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
    borderWidth: 0,
    borderRadius: 5,
    shadowOpacity: 0,
    backgroundColor: theme.CARD_BACKGROUND
  },
  weekCalendarHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: 20
  },
  viewText: {
    paddingVertical: 10,
    fontSize: 12
  },
  backToToday: {
    marginLeft: 20
  }
})

Calendar.propTypes = {
  events: PropTypes.array.isRequired,
  weekView: PropTypes.bool,
  noShadow: PropTypes.bool,
  onPressView: PropTypes.func,
  currentDate: PropTypes.string,
  onChangeDate: PropTypes.func
}

Calendar.defaultProps = {
  events: [],
  weekView: false,
  noShadow: false,
  onPressView: () => {},
  currentDate: '',
  onChangeDate: () => {}
}
